/** @format */
import { StateFrom, createMachine } from 'xstate';
import { AnyStateMachine } from 'xstate/lib/types';

export enum CheckoutMachineAction {
    EDIT_YOUR_DETAILS = 'edit-your-details',
    EDIT_BILLING_ADDRESS = 'edit-billing-address',
    EDIT_SHIPPING_DETAILS = 'edit-shipping-details',
    EDIT_EXTENDED_WARRANTY = 'edit-extended-warranty',
    EDIT_PAYMENT = 'edit-payment',
    SUBMIT_YOUR_DETAILS = 'submit-your-details',
    SUBMIT_BILLING_ADDRESS = 'submit-billing-address',
    SUBMIT_BILLING_ADDRESS_NO_SHIPPING = 'submit-billing-address-no-shipping',
    SUBMIT_SHIPPING_DETAILS = 'submit-shipping-details',
    SUBMIT_EXTENDED_WARRANTY = 'submit-extended-warranty',
    SKIP_EXTENDED_WARRANTY = 'skip-extended-warranty',
    SUBMIT_PAYMENT = 'submit-payment',
    INVALID_STATE_ACTION = 'invalid-state',
    SIGNED_UP_ACTION = 'signed-up',
    // When a transaction has been loaded on the website in the wrong market
    INVALID_MARKET_LOADED = 'invalid-market-loaded',
    INIT_YOUR_DETAILS = 'init-your-details',
    INIT_BILLING_ADDRESS = 'init-billing-address',
    INIT_SHIPPING_DETAILS = 'init-shipping-details',
    INIT_EXTENDED_WARRANTY = 'init-extended-warranty',
    INIT_PAYMENT = 'init-payment',
    SET_LOADING = 'loading',
}

export enum CheckoutMachineState {
    LOADING = 'loading',
    YOUR_DETAILS = 'your-details',
    BILLING_ADDRESS = 'billing-address',
    SHIPPING_DETAILS = 'shipping-details',
    AWAITING_CALCULATION = 'awaiting-calculation',
    EXTENDED_WARRANTY = 'extended-warranty',
    PAYMENT = 'payment',
    COMPLETE = 'complete',
    INVALID_STATE = 'invalid-state',
    INVALID_MARKET = 'invalid-market',
    SIGNED_UP = 'signed-up',
}

export enum CheckoutMachineSectionAction {
    SUCCEEDED = 'success',
    FAILED = 'failure',
}

export enum CheckoutMachineEntryActions {
    CLEAR_BALANCE_PAYMENT = 'clear-balance-payment',
}

const editActions = {
    [CheckoutMachineAction.SET_LOADING]: CheckoutMachineState.LOADING,
    [CheckoutMachineAction.EDIT_YOUR_DETAILS]:
        CheckoutMachineState.YOUR_DETAILS,
    [CheckoutMachineAction.EDIT_BILLING_ADDRESS]:
        CheckoutMachineState.BILLING_ADDRESS,
    [CheckoutMachineAction.EDIT_SHIPPING_DETAILS]:
        CheckoutMachineState.SHIPPING_DETAILS,
    [CheckoutMachineAction.EDIT_EXTENDED_WARRANTY]:
        CheckoutMachineState.EXTENDED_WARRANTY,
    [CheckoutMachineAction.EDIT_PAYMENT]: CheckoutMachineState.PAYMENT,
};

const initActions = {
    [CheckoutMachineAction.INIT_YOUR_DETAILS]:
        CheckoutMachineState.YOUR_DETAILS,
    [CheckoutMachineAction.INIT_BILLING_ADDRESS]:
        CheckoutMachineState.BILLING_ADDRESS,
    [CheckoutMachineAction.INIT_SHIPPING_DETAILS]:
        CheckoutMachineState.SHIPPING_DETAILS,
    [CheckoutMachineAction.INIT_EXTENDED_WARRANTY]:
        CheckoutMachineState.EXTENDED_WARRANTY,
    [CheckoutMachineAction.INIT_PAYMENT]: CheckoutMachineState.PAYMENT,
    [CheckoutMachineAction.INVALID_STATE_ACTION]:
        CheckoutMachineState.INVALID_STATE,
    [CheckoutMachineAction.INVALID_MARKET_LOADED]:
        CheckoutMachineState.INVALID_MARKET,
};

const machineId = 'checkout-machine';

type SectionStateConfig = {
    submitAction: CheckoutMachineAction;
    onSuccess: CheckoutMachineState;
}[];

const submittingState = 'submitting';

export function isCheckoutSectionSubmitting(
    current: StateFrom<AnyStateMachine>
) {
    return current.toStrings().pop()?.includes(submittingState) ?? false;
}

/**
 * Helper used to create child states and actions for each parent state passed.
 * We use it to add a child 'Submitting' state,
 * that responds to 'Success' and 'Failure' actions
 *
 * Example output:
 *
 * getSectionConfig([
 *   {
 *     submitAction: 'SUBMIT_YOUR_DETAILS',
 *     onSuccess: 'BILLING_ADDRESS'
 *   }
 * ])
 *
 * {
 *   initial: 'form',
 *   form: {
 *     on: {
 *       SUBMIT_YOUR_DETAILS: 'SUBMIT_YOUR_DETAILS.submitting'
 *     }
 *   },
 *   'SUBMIT_YOUR_DETAILS.submitting': {
 *     on: {
 *       success: `#${machineId}.BILLING_ADDRESS`,
 *       failure: 'form'
 *     }
 *   }
 * }
 */
function getSectionConfig(
    stateConfig: SectionStateConfig,
    actions?: Partial<Record<CheckoutMachineAction, CheckoutMachineState>>
) {
    const states = stateConfig.reduce(
        (currentStates, { submitAction, onSuccess }) => {
            const action = `${submitAction}-${submittingState}`;
            // @ts-ignore - Element implicitly has an any type - Touch and Change
            currentStates.form.on[submitAction] = action;
            // @ts-ignore - Element implicitly has an any type - Touch and Change
            currentStates[action] = {
                on: {
                    [CheckoutMachineSectionAction.SUCCEEDED]: `#${machineId}.${onSuccess}`,
                    [CheckoutMachineSectionAction.FAILED]: 'form',
                },
            };
            return currentStates;
        },
        {
            form: {
                on: {},
            },
        }
    );

    return {
        initial: 'form',
        on: {
            ...editActions,
            ...actions,
        },
        states,
        entry: [{ type: CheckoutMachineEntryActions.CLEAR_BALANCE_PAYMENT }],
    };
}

export const machineConfig = {
    predictableActionArguments: true,
    id: machineId,
    initial: CheckoutMachineState.LOADING,
    states: {
        [CheckoutMachineState.LOADING]: {
            on: {
                ...initActions,
            },
        },
        [CheckoutMachineState.YOUR_DETAILS]: {
            ...getSectionConfig([
                {
                    submitAction: CheckoutMachineAction.SUBMIT_YOUR_DETAILS,
                    onSuccess: CheckoutMachineState.BILLING_ADDRESS,
                },
            ]),
        },
        [CheckoutMachineState.BILLING_ADDRESS]: {
            ...getSectionConfig([
                {
                    submitAction: CheckoutMachineAction.SUBMIT_BILLING_ADDRESS,
                    onSuccess: CheckoutMachineState.SHIPPING_DETAILS,
                },
                {
                    submitAction:
                        CheckoutMachineAction.SUBMIT_BILLING_ADDRESS_NO_SHIPPING,
                    onSuccess: CheckoutMachineState.PAYMENT,
                },
            ]),
        },
        [CheckoutMachineState.SHIPPING_DETAILS]: {
            ...getSectionConfig([
                {
                    submitAction: CheckoutMachineAction.SUBMIT_SHIPPING_DETAILS,
                    onSuccess: CheckoutMachineState.EXTENDED_WARRANTY,
                },
            ]),
        },
        [CheckoutMachineState.EXTENDED_WARRANTY]: {
            ...getSectionConfig([
                {
                    submitAction:
                        CheckoutMachineAction.SUBMIT_EXTENDED_WARRANTY,
                    onSuccess: CheckoutMachineState.PAYMENT,
                },
                {
                    submitAction: CheckoutMachineAction.SKIP_EXTENDED_WARRANTY,
                    onSuccess: CheckoutMachineState.PAYMENT,
                },
            ]),
        },
        [CheckoutMachineState.PAYMENT]: {
            on: {
                [CheckoutMachineAction.SUBMIT_PAYMENT]:
                    CheckoutMachineState.COMPLETE,
                [CheckoutMachineAction.INIT_EXTENDED_WARRANTY]:
                    CheckoutMachineState.EXTENDED_WARRANTY,
                ...editActions,
            },
        },
        [CheckoutMachineState.COMPLETE]: {
            on: {
                [CheckoutMachineAction.SIGNED_UP_ACTION]:
                    CheckoutMachineState.SIGNED_UP,
            },
        },
        [CheckoutMachineState.INVALID_STATE]: {},
        [CheckoutMachineState.INVALID_MARKET]: {},
        [CheckoutMachineState.SIGNED_UP]: {},
    },
};

export const getCheckoutMachine = (options: {
    actions: Record<CheckoutMachineEntryActions, () => void>;
}) => createMachine(machineConfig, options);
